import head_p from '@/assets/img/chat/head_p.png'
import head_z from '@/assets/img/chat/head_z.png'
import moment from 'moment'
import axios from 'axios'
import { getToken, TokenKey } from '@/utils/auth'

const ROLE_AVATAR_MAP = {
  PATIENT: head_p,
  ASSISTANT: head_z,
  DOCTOR: head_z
}

export const MSG_TYPE = {
  TEXT: 'text',
  IMAGE: 'image',
  EMOTION: 'emotion',
  VIDEO: 'video',
  VOICE: 'voice',
  FILE: 'file'
}

const IMAGE_MSG_TYPES = [MSG_TYPE.IMAGE, MSG_TYPE.EMOTION]

const FILE_IMAGE_EXTS = ['png', 'jpg', 'jpeg']
const FILE_PREVIEW_EXTS = ['pdf']
const FILE_DOMAIN = 'https://file.drstrong.cn/frontend/wechatApplet/v1.0.0/static/cdn'

const FILE_ICON_MAP = {
  pdf: `${FILE_DOMAIN}/pdfIcon.png`,
  doc: `${FILE_DOMAIN}/wordIcon.png`,
  docx: `${FILE_DOMAIN}/wordIcon.png`,
  xls: `${FILE_DOMAIN}/xlsIcon.png`,
  xlsx: `${FILE_DOMAIN}/xlsxIcon.png`,
  zip: `${FILE_DOMAIN}/zipIcon.png`,
  defaultFile: `${FILE_DOMAIN}/defaultFile.png`
}

const MEDIA_URL_KEYS = ['url', 'fileUrl', 'mediaUrl', 'imageUrl', 'voiceUrl', 'videoUrl']

export function parseRecordData(msg = {}) {
  if (msg?.chatId || msg?.previewList) {
    return msg
  }
  try {
    const raw = msg?.data ?? msg?.content ?? msg?.toDa
    if (!raw) return {}
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch (e) {
    console.error('企微聊天记录解析失败:', e)
    return {}
  }
}

export function formatSenderLabel(item = {}) {
  const { sendName = '', sendRole } = item
  return `${sendRole}(${sendName})`
}

export function formatPreviewLine(item = {}) {
  const { sendName = '', sendContent = '' } = item
  if (!sendName && !sendContent) return ''
  return `${sendName}：${sendContent}`
}

export function formatMsgTime(sendTime) {
  if (!sendTime) return '--'
  const ts = Number(sendTime)
  if (ts) return moment(ts).format('YYYY-MM-DD HH:mm')
  const parsed = moment(sendTime)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '--'
}

export function getAvatarByRole(sendRole, memberType) {
  const normalizedRole = typeof sendRole === 'string' ? sendRole.toUpperCase() : sendRole
  if (normalizedRole && ROLE_AVATAR_MAP[normalizedRole]) {
    return ROLE_AVATAR_MAP[normalizedRole]
  }
  const type = Number(memberType)
  if (type === 1) return head_p
  if (type === 0) return head_z
  return head_z
}

export function parseMessageContent(content) {
  if (!content) return {}
  if (typeof content === 'string') {
    if (/^https?:\/\//.test(content)) return { url: content }
    try {
      const parsed = JSON.parse(content)
      return typeof parsed === 'object' && parsed ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof content === 'object') return content
  return {}
}

export function getMediaUrl(content) {
  const parsed = parseMessageContent(content)
  if (!parsed || typeof parsed !== 'object') return ''
  return MEDIA_URL_KEYS.map(key => parsed[key]).find(Boolean) || ''
}

export function getItemMediaUrl(item = {}) {
  return getMediaUrl(item.content)
}

export function isFileMediaReady(item = {}) {
  return Number(item.mediaStatus ?? 0) === 2
}

export function normalizeFileExt(ext = '') {
  return String(ext).replace(/^\./, '').toLowerCase()
}

export function getFileExt(content) {
  const parsed = parseMessageContent(content)
  return normalizeFileExt(parsed.fileExt || '')
}

export function isFileImageExt(ext) {
  return FILE_IMAGE_EXTS.includes(normalizeFileExt(ext))
}

export function isImageMsgType(msgType) {
  return IMAGE_MSG_TYPES.includes(msgType)
}

export function isFileImageMessage(item = {}) {
  return item.msgType === MSG_TYPE.FILE && isFileImageExt(getFileExt(item.content))
}

/** 列表内可点击预览的图片消息（image / emotion / 图片类 file） */
export function isInlinePreviewImage(item = {}) {
  const url = getItemMediaUrl(item)
  if (!url) return false
  return isImageMsgType(item.msgType) || isFileImageMessage(item)
}

/** 大图预览列表项（file 需 media 就绪） */
export function isPreviewableImage(item = {}) {
  const url = getItemMediaUrl(item)
  if (!url) return false
  if (isImageMsgType(item.msgType)) return true
  return (
    item.msgType === MSG_TYPE.FILE &&
    isFileMediaReady(item) &&
    isFileImageExt(getFileExt(item.content))
  )
}

export function isFileDocumentMessage(item = {}) {
  if (item.msgType !== MSG_TYPE.FILE) return false
  return !isFileImageExt(getFileExt(item.content))
}

export function getVoiceTranscript(content) {
  return parseMessageContent(content).transcript || ''
}

export function getVoicePlayLength(content) {
  return parseMessageContent(content).playLength
}

export function formatVoiceDuration(playLength) {
  const len = Number(playLength)
  if (Number.isFinite(len) && len >= 0) {
    return `${Math.round(len)}''`
  }
  return "0''"
}

function guessAudioMime(url = '') {
  const normalized = url.split('?')[0].toLowerCase()
  if (normalized.endsWith('.wav')) return 'audio/wav'
  if (normalized.endsWith('.ogg')) return 'audio/ogg'
  return 'audio/mpeg'
}

export async function resolveAuthMediaUrl(url = '') {
  if (!url) return ''
  try {
    if (isDrstrongFileUrl(url)) {
      const blob = await fetchFileBlob(url)
      if (!blob || !blob.size) return ''
      const typedBlob = blob.type ? blob : new Blob([blob], { type: guessAudioMime(url) })
      return URL.createObjectURL(typedBlob)
    }
    if (url.includes('@/assets/')) {
      return require(`@/assets/${url.replace('@/assets/', '')}`)
    }
    return url
  } catch (e) {
    console.error('媒体资源加载失败:', e)
    return ''
  }
}

export function shouldPreviewFileInBrowser(ext) {
  return FILE_PREVIEW_EXTS.includes(normalizeFileExt(ext))
}

export function isDrstrongFileUrl(url = '') {
  try {
    const { hostname } = new URL(url)
    return hostname === 'drstrong.cn' || hostname.endsWith('.drstrong.cn')
  } catch {
    return false
  }
}

async function fetchFileBlob(url) {
  const res = await axios.get(url, {
    responseType: 'blob',
    ...(isDrstrongFileUrl(url) ? { headers: { [TokenKey]: getToken() } } : {})
  })
  return res.data
}

function triggerBlobDownload(blob, fileName) {
  const blobUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName || 'file'
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(blobUrl)
}

export async function openFileByExt({ url, ext, fileName }) {
  if (!url) return
  const normalized = normalizeFileExt(ext)
  const name = fileName || url.split('/').pop() || 'file'

  try {
    const blob = await fetchFileBlob(url)
    if (shouldPreviewFileInBrowser(normalized)) {
      window.open(url, '_blank')
      return
    } else {
      triggerBlobDownload(blob, name)
    }
  } catch (e) {
    console.error('文件打开失败:', e)
    if (shouldPreviewFileInBrowser(normalized)) {
      window.open(url, '_blank')
      return
    }
    const link = document.createElement('a')
    link.href = url
    link.download = name
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export function getFileIconByExt(ext) {
  const normalized = normalizeFileExt(ext)
  return FILE_ICON_MAP[normalized] || FILE_ICON_MAP.defaultFile
}

export function getFileDisplayName(item = {}) {
  const content = parseMessageContent(item.content)
  if (content.fileName) return content.fileName
  const url = getItemMediaUrl(item)
  if (!url) return item.sendContent || '文件'
  return url.split('/').pop() || '文件'
}

const FILE_NAME_TAIL_CHARS = 3
const FILE_NAME_ELLIPSIS = '...'
// 两行展示宽度（中文计 2、英文计 1，约 18 * 2）
const FILE_NAME_MAX_DISPLAY_WIDTH = 36

function getCharDisplayWidth(char) {
  return /[^\x00-\xff]/.test(char) ? 2 : 1
}

export function estimateFileNameDisplayWidth(fileName = '') {
  let width = 0
  for (const char of String(fileName)) {
    width += getCharDisplayWidth(char)
  }
  return width
}

export function splitFileName(fileName = '') {
  const name = String(fileName)
  const lastDot = name.lastIndexOf('.')
  if (lastDot <= 0) {
    return { baseName: name, ext: '' }
  }
  return {
    baseName: name.slice(0, lastDot),
    ext: name.slice(lastDot)
  }
}

/** 文件名最多两行：超出时保留后缀与纯文件名末 3 字，中间用省略号 */
export function formatFileNameForDisplay(fileName = '', maxWidth = FILE_NAME_MAX_DISPLAY_WIDTH) {
  const name = String(fileName)
  if (!name || estimateFileNameDisplayWidth(name) <= maxWidth) return name

  const { baseName, ext } = splitFileName(name)
  const tailLen = Math.min(FILE_NAME_TAIL_CHARS, baseName.length)
  const tail = baseName.slice(-tailLen)
  const head = baseName.slice(0, baseName.length - tailLen)
  const suffixPart = `${FILE_NAME_ELLIPSIS}${tail}${ext}`
  const maxHeadWidth = Math.max(0, maxWidth - estimateFileNameDisplayWidth(suffixPart))

  let displayHead = ''
  let headWidth = 0
  for (const char of head) {
    const charWidth = getCharDisplayWidth(char)
    if (headWidth + charWidth > maxHeadWidth) break
    displayHead += char
    headWidth += charWidth
  }

  return `${displayHead}${suffixPart}`
}

export function formatFileSize(size) {
  const bytes = Number(size)
  if (!Number.isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

export function getFileSizeDisplay(content) {
  return formatFileSize(parseMessageContent(content).size)
}

export function resolveFileItem(item = {}) {
  const ext = getFileExt(item.content)
  const name = getFileDisplayName(item)
  return {
    url: getItemMediaUrl(item),
    ext,
    name,
    displayName: formatFileNameForDisplay(name),
    size: getFileSizeDisplay(item.content),
    icon: getFileIconByExt(ext),
    mediaReady: isFileMediaReady(item)
  }
}

export function getMessageKey(item = {}, index = 0) {
  return item.msgId || `${item.seq ?? index}`
}

export function parseHasNext(value) {
  if (value === true || value === 1) return true
  if (typeof value === 'string') {
    return value === 'true' || value === '1'
  }
  return false
}

export const defaultImage =
  'data:image/png;base64,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'
